import { Printer } from 'lucide-react';
import { Modal } from '../../../shared/components/Modal';
import { formatBRL, formatDateTime } from '../../../shared/lib/format';
import type { ResumoCaixa } from '../types';

interface Props {
  open: boolean;
  resumo: ResumoCaixa | null | undefined;
  onClose: () => void;
}

const FORMA_LABEL: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Cartao Debito',
  cartao_credito: 'Cartao Credito',
  vale_refeicao: 'Vale Refeicao',
  outro: 'Outro',
};

export function RelatorioZModal({ open, resumo, onClose }: Props) {
  if (!resumo) return null;
  const { caixa, por_forma, movimentos, qtd_vendas } = resumo;

  const totalDinheiro = Number(por_forma.dinheiro ?? 0);
  const totalReforco = movimentos
    .filter((m) => m.tipo === 'reforco')
    .reduce((a, m) => a + Number(m.valor), 0);
  const totalSangria = movimentos
    .filter((m) => m.tipo === 'sangria')
    .reduce((a, m) => a + Number(m.valor), 0);

  const valorSistema =
    caixa.valor_sistema != null
      ? Number(caixa.valor_sistema)
      : Number(caixa.valor_abertura) + totalDinheiro + totalReforco - totalSangria;

  const totalVendas = Object.values(por_forma).reduce((a, b) => a + Number(b), 0);

  const handlePrint = () => {
    const printable = document.getElementById('relatorio-z-print');
    if (!printable) return;
    const win = window.open('', '_blank', 'width=400,height=700');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8" />
      <title>Relatorio Z - Caixa ${caixa.id.slice(0, 8)}</title>
      <style>
        @media print { @page { margin: 5mm; size: 80mm auto; } }
        body { font-family: 'Courier New', monospace; font-size: 12px; margin: 10px; }
        h1 { font-size: 14px; text-align: center; margin: 4px 0; }
        hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
        .row { display: flex; justify-content: space-between; margin: 2px 0; }
        .bold { font-weight: bold; }
        .center { text-align: center; }
      </style></head><body>${printable.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 200);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Relatorio Z (fechamento de caixa)"
      size="md"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button className="btn-primary" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Imprimir
          </button>
        </>
      }
    >
      <div
        id="relatorio-z-print"
        className="space-y-2 rounded-lg bg-slate-50 p-4 font-mono text-sm text-slate-900"
      >
        <h1 className="bold center">FASTPDV - RELATORIO Z</h1>
        <hr />
        <div className="row">
          <span>Operador:</span>
          <span>{caixa.operador?.nome ?? caixa.operador?.email ?? caixa.operador_id.slice(0, 8)}</span>
        </div>
        <div className="row">
          <span>Abertura:</span>
          <span>{formatDateTime(caixa.abertura_em)}</span>
        </div>
        {caixa.fechamento_em && (
          <div className="row">
            <span>Fechamento:</span>
            <span>{formatDateTime(caixa.fechamento_em)}</span>
          </div>
        )}
        <div className="row">
          <span>Status:</span>
          <span className="capitalize">{caixa.status}</span>
        </div>
        <hr />

        <div className="row bold">
          <span>VENDAS ({qtd_vendas})</span>
          <span>{formatBRL(totalVendas)}</span>
        </div>
        {Object.entries(por_forma).length === 0 ? (
          <p className="text-slate-600">Nenhuma venda registrada</p>
        ) : (
          Object.entries(por_forma).map(([forma, valor]) => (
            <div className="row" key={forma}>
              <span>· {FORMA_LABEL[forma] ?? forma}</span>
              <span>{formatBRL(Number(valor))}</span>
            </div>
          ))
        )}

        <hr />
        <div className="row bold">MOVIMENTACOES</div>
        {movimentos.length === 0 ? (
          <p className="text-slate-600">Nenhuma sangria ou reforco</p>
        ) : (
          movimentos.map((m) => (
            <div className="row" key={m.id}>
              <span>
                {m.tipo === 'reforco' ? '+ Reforco' : '- Sangria'}
                {m.motivo ? ` (${m.motivo})` : ''}
              </span>
              <span>
                {m.tipo === 'reforco' ? '+' : '-'}
                {formatBRL(Number(m.valor))}
              </span>
            </div>
          ))
        )}

        <hr />
        <div className="row">
          <span>Abertura em gaveta</span>
          <span>{formatBRL(Number(caixa.valor_abertura))}</span>
        </div>
        <div className="row">
          <span>+ Dinheiro recebido</span>
          <span>{formatBRL(totalDinheiro)}</span>
        </div>
        <div className="row">
          <span>+ Reforcos</span>
          <span>{formatBRL(totalReforco)}</span>
        </div>
        <div className="row">
          <span>- Sangrias</span>
          <span>{formatBRL(totalSangria)}</span>
        </div>
        <div className="row bold">
          <span>= Esperado em gaveta</span>
          <span>{formatBRL(valorSistema)}</span>
        </div>

        {caixa.valor_informado != null && (
          <>
            <hr />
            <div className="row">
              <span>Valor contado</span>
              <span>{formatBRL(Number(caixa.valor_informado))}</span>
            </div>
            <div className="row bold">
              <span>Divergencia</span>
              <span>
                {(caixa.valor_divergencia ?? 0) >= 0 ? '+' : ''}
                {formatBRL(Number(caixa.valor_divergencia ?? 0))}
              </span>
            </div>
          </>
        )}

        {caixa.observacao && (
          <>
            <hr />
            <div>Obs: {caixa.observacao}</div>
          </>
        )}

        <hr />
        <p className="center">--- fim do relatorio ---</p>
      </div>
    </Modal>
  );
}
